/** Human-readable lifecycle and delivery reason mapping (mirrors backend). */

const REASON_MESSAGES: Record<string, { title: string; message: string; action?: string }> = {
  subscription_expired: {
    title: 'Subscription expired',
    message: 'Your subscription has expired. Renew to continue monitoring and receiving alerts.',
    action: 'Renew subscription',
  },
  payment_failed: {
    title: 'Payment failed',
    message: 'We could not process your latest payment. Update your payment method to avoid interruption.',
    action: 'Update payment',
  },
  grace_period: {
    title: 'Grace period',
    message: 'Your subscription is in a grace period. Renew before it ends to keep searches running.',
    action: 'Renew now',
  },
  notification_quota_exceeded: {
    title: 'Daily notification limit reached',
    message: 'Daily notification limit reached for this workspace plan.',
    action: 'View usage',
  },
  quota_exceeded: {
    title: 'Plan limit reached',
    message: 'You have reached a plan limit for this resource.',
    action: 'View usage',
  },
  quota_scrape_interval: {
    title: 'Scrape frequency limited',
    message: 'Your current plan limits how often this search can run.',
    action: 'View plan',
  },
  frequency_limit: {
    title: 'Scrape frequency limited',
    message: 'Your current plan allows scraping at a limited interval.',
    action: 'View plan',
  },
  source_disabled: {
    title: 'Source disabled',
    message: 'One or more sources for this search are disabled.',
    action: 'View sources',
  },
  manual: {
    title: 'Paused manually',
    message: 'This search was paused manually and will stay paused until you enable it.',
    action: 'Enable search',
  },
  disabled_by_user: {
    title: 'Paused by you',
    message: 'You disabled this search.',
    action: 'Enable search',
  },
  admin_disabled: {
    title: 'Paused by administrator',
    message: 'An administrator paused this search.',
    action: 'Contact support',
  },
  organization_disabled: {
    title: 'Organization inactive',
    message: 'Your organization is not active.',
    action: 'Contact support',
  },
};

export function humanizeReason(code: string | null | undefined): {
  title: string;
  message: string;
  action: string;
} {
  const key = (code || '').trim().toLowerCase();
  if (!key) {
    return { title: 'Unknown', message: 'No reason provided.', action: '' };
  }
  const entry = REASON_MESSAGES[key];
  if (entry) {
    return {
      title: entry.title,
      message: entry.message,
      action: entry.action ?? '',
    };
  }
  return {
    title: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    message: key.replace(/_/g, ' '),
    action: '',
  };
}

export function formatDeliveryError(
  errorMessage: string | null | undefined,
  friendlyMessage?: string | null
): string {
  if (friendlyMessage) return friendlyMessage;
  if (!errorMessage) return '';
  return humanizeReason(errorMessage).message;
}

export const LIFECYCLE_STATE_LABELS: Record<string, string> = {
  trial: 'Trial',
  active: 'Active',
  expiring_30: 'Expiring in 30 days',
  expiring_14: 'Expiring in 14 days',
  expiring_7: 'Expiring in 7 days',
  expiring_3: 'Expiring in 3 days',
  expiring_1: 'Expiring tomorrow',
  grace: 'Grace period',
  expired: 'Expired',
  cancelled: 'Cancelled',
  payment_failed: 'Payment failed',
  renewed: 'Renewed',
  revoked: 'Revoked',
};
